<script setup lang="ts">
  import { computed, provide, ref, watch } from 'vue'
  import { ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import { getComponent } from '@/components/questionaire/componentTypesV3'
  import RepeatSectionPrompt from '@/components/questionaire/formElements/RepeatSectionPrompt.vue'
  import FieldHelp from '@/components/questionaire/formElements/fieldHelp.vue'
  import { useQuestionnairePreviewStore } from '@/store/questionnairePreviewStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { FieldTypeEnum } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      previous: 'Previous',
      next: 'Next',
      previewComplete: 'Preview Complete',
      previewCompleteDescription:
        'You have reached the end of the questionnaire. The flow is working correctly.',
      restartPreview: 'Restart Preview',
      questionOf: 'Question {current} of {total}',
      showMore: 'Show more',
      showLess: 'Show less'
    },
    de: {
      previous: 'Zurück',
      next: 'Weiter',
      previewComplete: 'Vorschau abgeschlossen',
      previewCompleteDescription:
        'Sie haben das Ende des Fragebogens erreicht. Der Ablauf funktioniert korrekt.',
      restartPreview: 'Vorschau neu starten',
      questionOf: 'Frage {current} von {total}',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen'
    }
  })

  const emit = defineEmits<{
    restart: []
  }>()

  const previewStore = useQuestionnairePreviewStore()
  const themeColor = computed(() => useThemeColor().value)
  const isHelpTextExpanded = ref(false)

  // Computed classes for the Next button to avoid Tailwind JIT issues with dynamic class strings
  const nextButtonClasses = computed(() => {
    const baseClasses =
      'flex h-8 w-[100px] items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium text-white shadow-sm'

    // Map theme colors to their full class strings (Tailwind JIT needs complete strings)
    const themeClassMap: Record<string, string> = {
      cardinal: 'bg-cardinal-600 hover:bg-cardinal-700 focus-visible:ring-cardinal-600',
      rust: 'bg-rust-600 hover:bg-rust-700 focus-visible:ring-rust-600',
      forest: 'bg-forest-600 hover:bg-forest-700 focus-visible:ring-forest-600',
      jade: 'bg-jade-600 hover:bg-jade-700 focus-visible:ring-jade-600',
      azure: 'bg-azure-600 hover:bg-azure-700 focus-visible:ring-azure-600',
      cobalt: 'bg-cobalt-600 hover:bg-cobalt-700 focus-visible:ring-cobalt-600',
      iris: 'bg-iris-600 hover:bg-iris-700 focus-visible:ring-iris-600',
      orchid: 'bg-orchid-600 hover:bg-orchid-700 focus-visible:ring-orchid-600',
      crimson: 'bg-crimson-600 hover:bg-crimson-700 focus-visible:ring-crimson-600'
    }

    const themeClasses = themeClassMap[themeColor.value] || themeClassMap.iris
    return `${baseClasses} ${themeClasses}`
  })

  // Map field type to component name
  function getFieldComponentName(fieldType: string): string {
    const componentMap: Record<string, string> = {
      [FieldTypeEnum.TEXTINPUT]: 'textinput',
      [FieldTypeEnum.TEXTAREA]: 'textarea',
      [FieldTypeEnum.EMAILINPUT]: 'emailinput',
      [FieldTypeEnum.PHONEINPUT]: 'phoneinput',
      [FieldTypeEnum.NUMBERINPUT]: 'numberinput',
      [FieldTypeEnum.DECIMALNUMBERINPUT]: 'decimalnumberinput',
      [FieldTypeEnum.URLINPUT]: 'urlinput',
      [FieldTypeEnum.DATEFIELD]: 'datefield',
      [FieldTypeEnum.TIMEFIELD]: 'timefield',
      [FieldTypeEnum.SELECT]: 'select',
      [FieldTypeEnum.MULTISELECT]: 'multiselect',
      [FieldTypeEnum.YESNO]: 'yesno',
      [FieldTypeEnum.FILEFIELD]: 'filefield',
      [FieldTypeEnum.MULTIFILEFIELD]: 'multifilefield'
    }
    return componentMap[fieldType] || 'textinput'
  }

  // Get the active field with component
  const activeField = computed(() => {
    const field = previewStore.activeField
    if (!field) return null

    return {
      ...field,
      component: getComponent(getFieldComponentName(field.type)),
      choices: field.choices || []
    }
  })

  // Reset help text expansion when field changes
  watch(
    () => previewStore.activeFieldId,
    () => {
      isHelpTextExpanded.value = false
    }
  )

  const isHelpTextTruncated = computed(() => {
    if (!activeField.value?.help) return false
    return activeField.value.help.length > 150
  })

  function toggleHelpText() {
    isHelpTextExpanded.value = !isHelpTextExpanded.value
  }

  // Progress indicator
  const progressText = computed(() => {
    return t('questionOf', {
      current: previewStore.activeFieldIndex + 1,
      total: previewStore.formLength
    })
  })

  // Handle next button click
  function handleNext() {
    // Check if we're at the last field of a repeatable section
    if (previewStore.isLastFieldOfRepeatableSection) {
      const sectionInfo = previewStore.currentRepeatableSectionInfo
      if (sectionInfo && previewStore.canAddMoreRepetitions(sectionInfo)) {
        previewStore.setRepeatSectionPrompt(true, sectionInfo)
        return
      }
    }

    previewStore.goToNextField()
  }

  // Handle previous button click
  function handlePrevious() {
    previewStore.goToPreviousField()
  }

  // Handle add another section
  function handleAddAnother() {
    previewStore.duplicateSectionForPreview()
  }

  // Handle continue without repeat
  function handleContinue() {
    previewStore.handleContinueWithoutRepeat()
  }

  // Handle restart
  function handleRestart() {
    previewStore.resetPreview()
    emit('restart')
  }

  // Provide vueform context for field components
  provide('vueform', {
    formState: ref({
      valid: true,
      activeFieldId: previewStore.activeFieldId,
      isLastField: previewStore.isLastField,
      isFirstField: previewStore.isFirstField
    }),
    fieldValidationErrors: ref({}),
    validateField: () => true,
    handleFieldValidationError: () => {},
    saveFieldData: () => {},
    onSubmit: () => {},
    back: handlePrevious,
    handleFieldChange: (uuid: string, value: any) => {
      previewStore.updateFormData(uuid, value)
    }
  })
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Progress indicator -->
    <div
      v-if="!previewStore.isPreviewComplete && !previewStore.showRepeatSectionPrompt"
      class="mb-4 flex items-center justify-between"
    >
      <Badge
        variant="secondary"
        class="text-xs"
      >
        {{ progressText }}
      </Badge>
    </div>

    <!-- Main content area -->
    <div class="flex flex-1 items-center justify-center">
      <!-- Repeat Section Prompt -->
      <div
        v-if="previewStore.showRepeatSectionPrompt && previewStore.pendingRepeatSectionInfo"
        class="w-full max-w-[560px]"
      >
        <RepeatSectionPrompt
          :participant-type="previewStore.pendingRepeatSectionInfo.participantType || ''"
          @add-another="handleAddAnother"
          @continue="handleContinue"
        />
      </div>

      <!-- Preview Complete Screen -->
      <div
        v-else-if="previewStore.isPreviewComplete"
        class="flex w-full max-w-[560px] flex-col items-center justify-center gap-6 rounded-xl border
          border-gray-200 bg-white p-8 shadow-sm"
      >
        <div class="flex flex-col items-center gap-4">
          <div class="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 class="h-8 w-8 text-green-600" />
          </div>
          <div class="flex flex-col items-center gap-2 text-center">
            <h2 class="text-xl font-semibold text-gray-900">
              {{ t('previewComplete') }}
            </h2>
            <p class="text-sm text-gray-600">
              {{ t('previewCompleteDescription') }}
            </p>
          </div>
        </div>

        <Button
          variant="secondary"
          class="flex items-center gap-2"
          @click="handleRestart"
        >
          {{ t('restartPreview') }}
        </Button>
      </div>

      <!-- Question Card -->
      <div
        v-else-if="activeField"
        class="w-full max-w-[560px]"
      >
        <div
          class="flex min-h-[360px] flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div class="flex flex-col">
            <!-- Question label and help -->
            <div class="flex flex-col gap-1">
              <h2 class="text-lg font-medium leading-tight text-gray-900">
                {{ activeField.label }}
                <span
                  v-if="!activeField.optional"
                  class="text-red-500"
                >
                  *
                </span>
              </h2>
              <div
                v-if="activeField.help"
                class="flex flex-col gap-1"
              >
                <div
                  :class="[
                    'text-sm font-normal leading-tight text-gray-600 transition-all duration-200',
                    !isHelpTextExpanded && isHelpTextTruncated ? 'line-clamp-3' : ''
                  ]"
                >
                  <FieldHelp :help-text="activeField.help" />
                </div>
                <button
                  v-if="isHelpTextTruncated"
                  class="self-start text-sm font-medium text-purple-600 underline transition-colors hover:text-purple-800"
                  type="button"
                  @click="toggleHelpText"
                >
                  {{ isHelpTextExpanded ? t('showLess') : t('showMore') }}
                </button>
              </div>
            </div>

            <!-- Field component -->
            <div class="mt-4 w-full">
              <Component
                :is="activeField.component"
                v-if="activeField.component"
                v-model="previewStore.formData[activeField.uuid]"
                :field="activeField"
                :is-preview-mode="true"
              />
            </div>
          </div>

          <!-- Navigation buttons -->
          <div class="mt-4 flex justify-end gap-2">
            <Button
              variant="secondary"
              class="flex h-8 items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium shadow-sm"
              :disabled="previewStore.isFirstField"
              @click="handlePrevious"
            >
              <ChevronLeft class="h-5 w-5" />
              <span>{{ t('previous') }}</span>
            </Button>
            <Button
              :class="nextButtonClasses"
              @click="handleNext"
            >
              <span>{{ t('next') }}</span>
              <ChevronRight class="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
